<template>
  <slot />
</template>

<script setup lang="ts">
import { computed, provide, type Ref, toRef } from 'vue';
import { useRoute } from 'vue-router';
import { useIsFetching } from '@tanstack/vue-query';
import { useOrganizationSettingsQuery } from '@/composables/queries/useOrganizationSettingsQuery';
import { useOrganizationAvailableModesQuery } from '@/composables/queries/useOrganizationAvailableModesQuery';
import { useOrganizationAccountingQuery } from '@/composables/queries/useOrganizationAccountingQuery';
import { useOrganizationBrandingQuery } from '@/composables/queries/useOrganizationBrandingQuery';
import {
  type OrganizationPageContext,
  organizationPageKey
} from '@/composables/useOrganizationContext';

interface Props {
  companyId: string | null | undefined;
  documentId: string | null | undefined;
}

const props = defineProps<Props>();
const route = useRoute();

const companyId: Ref<string | null | undefined> = toRef(props, 'companyId');
const documentId: Ref<string | null | undefined> = toRef(props, 'documentId');

const isCreateRoute = computed(() => route.name === 'document-create');
const isPublicCreateRoute = computed(() => route.name === 'p-document-create');
const isInviteRoute = computed(() => route.name === 'sign-id');
const isPublicViewRoute = computed(() => route.name === 'p-document-view');
const isSignRoute = computed(() => route.name === 'document-sign');
const isSealRoute = computed(() => route.name === 'document-seal');
const isDetailsRoute = computed(() => route.name === 'document-details');

const accountingQuery = useOrganizationAccountingQuery({
  companyId,
  enabled: isCreateRoute
});

const settingsQuery = useOrganizationSettingsQuery({
  documentId,
  enabled: computed(() => isCreateRoute.value || isPublicCreateRoute.value)
});

const availableModesQuery = useOrganizationAvailableModesQuery({
  companyId,
  enabled: computed(() => isCreateRoute.value || isPublicCreateRoute.value)
});

const brandingsQuery = useOrganizationBrandingQuery({
  documentId,
  enabled: computed(
    () =>
      isCreateRoute.value ||
      isPublicCreateRoute.value ||
      isSignRoute.value ||
      isDetailsRoute.value ||
      isSealRoute.value ||
      isInviteRoute.value ||
      isPublicViewRoute.value
  )
});

const isFetchingUsingCompanyId = useIsFetching({
  queryKey: computed(() => ['organization', companyId.value])
});

const isFetchingUsingDocumentId = useIsFetching({
  queryKey: computed(() => ['organization', documentId.value])
});

const isLoadingOrganizationData = computed(
  () =>
    isFetchingUsingDocumentId.value > 0 || isFetchingUsingCompanyId.value > 0
);

const context: OrganizationPageContext = {
  settingsQuery,
  availableModesQuery,
  accountingQuery,
  brandingsQuery,
  isLoadingOrganizationData
};

provide(organizationPageKey, context);
</script>
